import React, { useEffect, useState, useCallback } from 'react';
import { Box, Button, Typography, Paper, Alert, CircularProgress, Stack } from '@mui/material';
import { Navigate, useNavigate, useLocation } from 'react-router-dom';

import { useAuth } from '../context/AuthContext';
import { brand, neutral, radius, shadow, border } from '../theme/tokens';
import Logo from '../components/ui/Logo';
import {
  shouldAutoLogin,
  hasAttemptedAutoLogin,
  markAutoLoginAttempted,
  hasExplicitlySignedOut,
  clearExplicitSignOut,
} from '../utils/autoLogin';

const isDevelopment = process.env.NODE_ENV === 'development';
const skipAuthEnv = isDevelopment && process.env.REACT_APP_SKIP_AUTH === 'true';

/**
 * Removes cached MSAL state. A failed or abandoned redirect can leave the
 * library convinced an interaction is still in flight, which blocks every
 * later sign-in attempt until the stale keys are cleared.
 */
const clearMsalState = () => {
  [sessionStorage, localStorage].forEach((store) => {
    try {
      Object.keys(store)
        .filter((key) => key.startsWith('msal.'))
        .forEach((key) => store.removeItem(key));
    } catch {
      /* storage can be unavailable in hardened browser settings */
    }
  });

  document.cookie.split(';').forEach((cookie) => {
    const trimmed = cookie.trim();
    if (trimmed.startsWith('msal.')) {
      document.cookie = `${trimmed.split('=')[0]}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    }
  });
};

const Login = () => {
  const { isAuthenticated, login, loading, devMode, authError } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState(null);
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  useEffect(() => {
    if (location.state?.error) setError(`Authentication error: ${location.state.error}`);
  }, [location.state]);

  useEffect(() => {
    if (authError) {
      setError(authError);
      setIsLoggingIn(false);
    }
  }, [authError]);

  useEffect(() => {
    if (devMode || skipAuthEnv || isAuthenticated) navigate('/dashboard', { replace: true });
  }, [devMode, navigate, isAuthenticated]);

  const handleLogin = useCallback(async () => {
    try {
      setError(null);
      setIsLoggingIn(true);
      // A deliberate click overrides a previous explicit sign-out.
      clearExplicitSignOut();
      clearMsalState();
      await login();
      // login() redirects, so nothing below normally runs.
    } catch (err) {
      const message = err?.message || '';
      setError(
        message.includes('uninitialized_public_client_application')
          ? 'Microsoft authentication failed to initialise. Clear your browser cache and cookies, then refresh.'
          : message || 'Failed to sign in. Please try again.'
      );
      setIsLoggingIn(false);
    }
  }, [login]);

  // Attempt SSO automatically instead of making the user click a button.
  //
  // Entra usually completes without interaction, so the button is a fallback.
  // shouldAutoLogin() enforces the guards that prevent a redirect loop; the
  // attempt is recorded in sessionStorage because the redirect reloads the
  // page and would otherwise restart the cycle forever.
  useEffect(() => {
    const go = shouldAutoLogin({
      loading,
      isAuthenticated,
      error,
      devMode,
      skipAuth: skipAuthEnv,
      alreadyAttempted: hasAttemptedAutoLogin(),
      explicitSignOut: hasExplicitlySignedOut(),
    });

    if (!go) return;

    markAutoLoginAttempted();
    setIsLoggingIn(true);
    login().catch((err) => {
      // Fall back to the visible button rather than leaving a dead spinner.
      setError(err?.message || 'Automatic sign-in failed. Please sign in manually.');
      setIsLoggingIn(false);
    });
  }, [loading, isAuthenticated, error, devMode, login]);

  const handleReset = useCallback(() => {
    clearMsalState();
    window.location.reload();
  }, []);

  if (isAuthenticated || devMode || skipAuthEnv) return <Navigate to="/dashboard" replace />;

  const busy = loading || isLoggingIn;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        p: 3,
        background: `radial-gradient(1200px 600px at 50% -10%, ${brand[900]} 0%, ${neutral[950]} 60%)`,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, sm: 5 },
          width: '100%',
          maxWidth: 440,
          borderRadius: `${radius.lg}px`,
          boxShadow: shadow.lg,
          border: `1px solid ${border.standard}`,
        }}
      >
        <Stack spacing={1} alignItems="center" sx={{ mb: 4, textAlign: 'center' }}>
          <Logo size={56} sx={{ mb: 1 }} />
          <Typography variant="h5" component="h1" fontWeight={700}>
            Squid
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Engineering effort analytics. Sign in with your ROLLER account to continue.
          </Typography>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
            {error.toLowerCase().includes('interaction') && (
              <Box sx={{ mt: 1.5 }}>
                <Button variant="outlined" color="error" size="small" onClick={handleReset}>
                  Reset authentication state
                </Button>
              </Box>
            )}
          </Alert>
        )}

        <Button
          variant="contained"
          size="large"
          fullWidth
          onClick={handleLogin}
          disabled={busy}
          startIcon={busy ? <CircularProgress size={18} color="inherit" /> : null}
        >
          {busy ? 'Signing in…' : 'Sign in with Microsoft'}
        </Button>

        {isDevelopment && (
          <Box sx={{ mt: 3 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              Development mode. Production builds always enforce Microsoft sign-in.
            </Alert>
            <Button
              variant="outlined"
              color="secondary"
              fullWidth
              onClick={() => {
                sessionStorage.setItem('devBypass', 'true');
                navigate('/dashboard', { replace: true });
              }}
            >
              Development bypass
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default Login;
